import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/site/ProductCard";
import { Reveal } from "@/components/site/Reveal";

export default function Products() {
    const { t } = useLang();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [active, setActive] = useState("All");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(["All", ...r.data])).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = active === "All" ? {} : { category: active };
        api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, [active]);

    const label = (c) => (c === "All" ? t.products.all : c);

    return (
        <div className="bg-background min-h-screen">
            <Header />
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-36 pb-16">
                <Reveal className="eyebrow text-cyan mb-6">{t.products.eyebrow}</Reveal>
                <Reveal delay={0.05}>
                    <h1 className="font-heading font-black text-5xl sm:text-7xl tracking-tighter text-obsidian leading-[0.9] max-w-3xl">
                        {t.products.title}
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 max-w-xl text-muted-foreground text-lg">{t.products.sub}</p>
                </Reveal>

                <div className="mt-12 flex flex-wrap gap-3" data-testid="category-filters">
                    {categories.map((c) => (
                        <button
                            key={c}
                            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setActive(c)}
                            className={`eyebrow rounded-full px-5 py-2.5 border transition-all duration-300 ${
                                active === c
                                    ? "bg-obsidian text-white border-obsidian"
                                    : "bg-transparent text-obsidian/60 border-border hover:border-obsidian hover:text-obsidian"
                            }`}
                        >
                            {label(c)}
                        </button>
                    ))}
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-32">
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] bg-secondary rounded-xl animate-pulse" />)}
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                        {products.map((p, i) => (
                            <Reveal key={p.slug} delay={i * 0.05}><ProductCard product={p} index={i} /></Reveal>
                        ))}
                    </div>
                )}
                {!loading && products.length === 0 && (
                    <p className="text-muted-foreground py-20 text-center">{t.products.empty}</p>
                )}
            </section>
            <Footer />
        </div>
    );
}
